import { SelectMenu } from '@/components/ds';
import { Popover } from '@heroui/react/popover';
import { Check, Layers, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBranch } from '@/contexts/BranchContext';
import { cn } from '@/utils/cn';

interface BranchSwitcherProps {
  collapsed?: boolean;
}

export function BranchSwitcher({ collapsed = false }: BranchSwitcherProps) {
  const { t } = useTranslation();
  const { branches, selectedBranch, selectBranch, canSelectAllBranches } =
    useBranch();

  /*
    The collapsed rail has no room for the select, so the picker opens
    as a flyout. This button previously rendered with no handler at
    all — it looked interactive but could not change the branch.
  */
  if (collapsed) {
    const options = [
      { id: 'all', name: t('nav.allBranches', 'All branches') },
      ...branches,
    ];
    const currentId = selectedBranch?.id || 'all';

    return (
      <div className="flex justify-center p-2">
        <Popover.Root>
          <Popover.Trigger<'button'>
            render={(props) => (
              <button
                {...props}
                type="button"
                title={t('nav.branchSelector')}
                aria-label={t('nav.branchSelector')}
                className="flex size-10 items-center justify-center rounded-md
                  text-brand-text transition-colors
                  duration-(--shell-motion-fast) hover:bg-surface-2"
              >
                <MapPin aria-hidden className="size-5" />
              </button>
            )}
          />
          <Popover.Content
            placement="end top"
            offset={8}
            className="w-56 p-1.5"
          >
            <p className="px-2.5 pb-1.5 pt-1 text-micro font-semibold uppercase tracking-wider text-secondary-400">
              {t('nav.currentBranch')}
            </p>
            <div className="max-h-[min(60vh,24rem)] space-y-0.5 overflow-y-auto scroll-thin">
              {options.map((branch) => {
                const isCurrent = branch.id === currentId;
                return (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => selectBranch(branch.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-start',
                      'text-small transition-colors duration-(--shell-motion-fast)',
                      isCurrent
                        ? 'bg-brand-soft font-semibold text-brand-text'
                        : 'text-secondary-600 hover:bg-surface-2',
                    )}
                  >
                    <MapPin aria-hidden className="size-4 shrink-0" />
                    <span className="flex-1 truncate">{branch.name}</span>
                    {isCurrent && (
                      <Check aria-hidden className="size-4 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </Popover.Content>
        </Popover.Root>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <label className="block text-micro font-semibold uppercase tracking-wider text-neutral-400 mb-[6px]">
        {t('nav.currentBranch')}
      </label>
      <SelectMenu
        value={selectedBranch?.id || 'all'}
        onChange={selectBranch}
        options={[
          // "All branches" is the consolidated view (converted to the base
          // currency when branches differ); the collapsed flyout offered it,
          // the expanded select did not.
          ...(canSelectAllBranches
            ? [
                {
                  value: 'all',
                  label: t('nav.allBranches', 'All branches'),
                  content: (
                    <span className="flex items-center gap-2">
                      <Layers className="size-4 shrink-0 text-primary-600" />
                      {t('nav.allBranches', 'All branches')}
                    </span>
                  ),
                },
              ]
            : []),
          ...branches.map((branch) => ({
            value: branch.id,
            label: branch.name,
            content: (
              <span className="flex items-center gap-2">
                <MapPin className="size-4 shrink-0 text-primary-600" />
                {branch.name}
              </span>
            ),
          })),
        ]}
      />
    </div>
  );
}
